import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Loader2, Wallet, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import {
  paperAccountService as svc,
  PaperAccountInUseError,
  type PaperAccount,
  type PaperAccountMode,
  type PaperAccountSummary,
} from '@/services/paperAccountService';

// Equity and Forex only: the broker registry has no Crypto factory, and the provider resolves
// a factory BEFORE reaching the simulated-client branch — so a Crypto account (even a
// credentials-free simulated one) resolves to null and every entry bound to it is silently
// skipped. Offering it would just be a trap.
const ASSET_CLASSES = ['Equity', 'Forex'];

// The broker registry keys factories by (assetClass, provider), so the provider has to match
// the venue: there is no Forex/Alpaca factory, and UserBrokerClientProvider resolves a factory
// BEFORE reaching its simulated-client branch — so a mismatch resolves to null and every entry
// bound to the account is silently skipped, even for a credentials-free simulated account.
const PROVIDER_FOR_ASSET_CLASS: Record<string, string> = {
  Equity: 'Alpaca',
  Forex: 'Fxcm',
};

function slugify(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
}

function money(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function signedMoney(n: number): string {
  const abs = Math.abs(n).toLocaleString(undefined, {
    style: 'currency', currency: 'USD', maximumFractionDigits: 2,
  });
  return n < 0 ? `−${abs}` : `+${abs}`;
}

function pnlClass(n: number): string {
  return n > 0 ? 'text-emerald-500' : n < 0 ? 'text-red-500' : 'text-muted-foreground';
}

/**
 * Configuration → Paper Accounts.
 *
 * A paper account is a named pot of capital a strategy binds to. Without one, every
 * strategy shares a single balance: they over-commit it, and because a buying-power
 * rejection pauses entries account-wide for 15 minutes, one strategy over-sizing blocks
 * the others. Binding each strategy to its own account makes that impossible.
 */
export const PaperAccountsSettingsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<PaperAccount[]>([]);
  const [summaries, setSummaries] = useState<Record<string, PaperAccountSummary>>({});
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setAccounts(await svc.list());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load paper accounts');
    } finally {
      setLoaded(true);
    }
    // Live rollups ride separately: a summaries failure (price feed hiccup, slow
    // aggregation) must not blank the account CONFIG cards, which are the point
    // of this page — the strip just keeps its last values.
    try {
      const s = await svc.summaries();
      setSummaries(Object.fromEntries(s.map((x) => [x.accountId, x])));
    } catch { /* keep previous summaries */ }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // The strip claims "live" numbers, so keep them honest with a slow poll —
  // 30s matches how fast paper P&L meaningfully moves, without hammering the
  // per-symbol price lookups behind the endpoint.
  useEffect(() => {
    const id = setInterval(() => { void refresh(); }, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  return (
    <div className="space-y-3 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold">Paper Accounts</h2>
        <p className="text-xs text-muted-foreground">
          Separate pots of paper capital. Bind a strategy to its own account so two strategies
          can't spend the same balance — or block each other when one hits a buying-power limit.
        </p>
      </div>

      {error && (
        <div className="text-xs text-red-500 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {error}
        </div>
      )}

      {!loaded ? (
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {accounts.length === 0 && !creating && (
            <Card>
              <CardContent className="py-6 text-center space-y-2">
                <div className="text-sm text-muted-foreground">
                  No paper accounts yet. Strategies use your shared paper account.
                </div>
              </CardContent>
            </Card>
          )}

          {accounts.map((a) => (
            <AccountCard
              key={a.accountId}
              account={a}
              summary={summaries[a.accountId] ?? null}
              onSaved={() => void refresh()}
              onDeleted={() => void refresh()}
            />
          ))}

          {creating ? (
            <AccountCard
              account={null}
              onSaved={() => { setCreating(false); void refresh(); }}
              onDeleted={() => setCreating(false)}
              existingIds={accounts.map((a) => a.accountId)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1.5 h-8 rounded-md border px-3 text-xs hover:bg-accent"
            >
              <Plus className="h-3.5 w-3.5" /> New paper account
            </button>
          )}
        </>
      )}
    </div>
  );
};

const AccountCard: React.FC<{
  account: PaperAccount | null;
  summary?: PaperAccountSummary | null;
  onSaved: () => void;
  onDeleted: () => void;
  existingIds?: string[];
}> = ({ account, summary = null, onSaved, onDeleted, existingIds = [] }) => {
  const isNew = account === null;

  const [name, setName] = useState(account?.name ?? '');
  const [mode, setMode] = useState<PaperAccountMode>(account?.mode ?? 'Simulated');
  const [startingEquity, setStartingEquity] = useState(String(account?.startingEquity ?? 10000));
  const [assetClass, setAssetClass] = useState(account?.assetClass ?? 'Equity');
  const [apiKey, setApiKey] = useState(account?.apiKey ?? '');
  const [apiSecret, setApiSecret] = useState('');
  const [enabled, setEnabled] = useState(account?.enabled ?? true);
  const [maxExposure, setMaxExposure] = useState(String(account?.maxExposureMultiple ?? 1));

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [blocking, setBlocking] = useState<string[]>([]);

  const save = async () => {
    setErr(null);
    setBlocking([]);

    const equity = Number(startingEquity);
    if (!name.trim()) { setErr('Name is required.'); return; }
    if (!Number.isFinite(equity) || equity <= 0) { setErr('Starting capital must be greater than zero.'); return; }

    const accountId = account?.accountId ?? slugify(name);
    if (!accountId) { setErr('Name must contain at least one letter or number.'); return; }
    if (isNew && existingIds.includes(accountId)) { setErr('An account with a similar name already exists.'); return; }

    setSaving(true);
    try {
      await svc.upsert(accountId, {
        name: name.trim(),
        mode,
        startingEquity: equity,
        assetClass,
        provider: PROVIDER_FOR_ASSET_CLASS[assetClass] ?? 'Alpaca',
        apiKey: mode === 'Broker' ? apiKey.trim() : '',
        // Blank keeps the stored secret — the API never returns it, so an unchanged
        // form posts back empty and must not wipe what's saved.
        apiSecret: mode === 'Broker' ? apiSecret : '',
        enabled,
        maxExposureMultiple: Number(maxExposure) || 1,
      });
      setApiSecret('');
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (isNew) { onDeleted(); return; }
    setErr(null);
    setBlocking([]);
    setSaving(true);
    try {
      await svc.remove(account!.accountId);
      onDeleted();
    } catch (e: any) {
      if (e instanceof PaperAccountInUseError) {
        setErr(e.message);
        setBlocking(e.strategies);
      } else {
        setErr(e?.message || 'Failed to delete');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="h-4 w-4 text-primary" />
          {isNew ? 'New paper account' : account!.name}
          {!isNew && !account!.enabled && (
            <span className="text-[11px] font-normal text-muted-foreground">(disabled)</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {summary && (
          <div className="rounded-md border bg-muted/30 px-3 py-2 space-y-1.5">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
              <span>
                <span className="text-muted-foreground">Equity </span>
                <span className="font-semibold tabular-nums">{money(summary.equity)}</span>
              </span>
              <span>
                <span className="text-muted-foreground">P&amp;L </span>
                <span className={`font-medium tabular-nums ${pnlClass(summary.realizedPnL + summary.unrealizedPnL)}`}>
                  {signedMoney(summary.realizedPnL + summary.unrealizedPnL)}
                </span>
                {summary.unrealizedPnL !== 0 && (
                  <span className="text-[11px] text-muted-foreground"> ({signedMoney(summary.unrealizedPnL)} open)</span>
                )}
              </span>
              <span className="text-muted-foreground tabular-nums">
                {summary.openTradeCount} open trade{summary.openTradeCount === 1 ? '' : 's'}
                {summary.openExposure > 0 && <> · {money(summary.openExposure)} exposure</>}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1 text-[11px]">
              {summary.strategies.length === 0 ? (
                <span className="text-muted-foreground">No strategies bound.</span>
              ) : (
                <>
                  <span className="text-muted-foreground">
                    {summary.accountId === 'default' ? 'Trading from this shared pot:' : 'Bound:'}
                  </span>
                  {summary.strategies.map((s) => (
                    <span
                      key={s.id}
                      className={`rounded-full border px-1.5 py-px ${s.enabled ? '' : 'opacity-50'}`}
                      title={s.enabled ? undefined : 'Strategy disabled'}
                    >
                      {s.name}
                    </span>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Minervini Tight — $10k"
              className="h-8 w-full rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>

          <Field label="Starting capital" hint="What this strategy is allowed to trade with.">
            <input
              type="number"
              min={1}
              value={startingEquity}
              onChange={(e) => setStartingEquity(e.target.value)}
              className="h-8 w-full rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>

          <Field
            label="Execution"
            hint={mode === 'Simulated'
              ? 'Fills are synthetic — no brokerage setup needed.'
              : 'Orders route to a real brokerage paper account with its own keys.'}
          >
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as PaperAccountMode)}
              className="h-8 w-full rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="Simulated">Simulated</option>
              <option value="Broker">Real broker paper account</option>
            </select>
          </Field>

          <Field label="Asset class" hint="Must match the strategies you bind to it.">
            <select
              value={assetClass}
              onChange={(e) => setAssetClass(e.target.value)}
              className="h-8 w-full rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {ASSET_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          {mode === 'Broker' && (
            <>
              <Field label="API key">
                <input
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="h-8 w-full rounded-md border bg-background px-2 text-xs font-mono outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </Field>
              <Field
                label="API secret"
                hint={account?.hasApiSecret ? 'Stored. Leave blank to keep it.' : 'Required.'}
              >
                <input
                  type="password"
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder={account?.hasApiSecret ? '••••••••' : ''}
                  className="h-8 w-full rounded-md border bg-background px-2 text-xs font-mono outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </Field>
            </>
          )}

          {mode === 'Simulated' && (
            <Field label="Max exposure" hint="Multiple of capital across open trades. 1 = no leverage.">
              <input
                type="number"
                min={0.1}
                step={0.1}
                value={maxExposure}
                onChange={(e) => setMaxExposure(e.target.value)}
                className="h-8 w-full rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </Field>
          )}
        </div>

        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
          <span className="text-[11px] text-muted-foreground">
            — disabling stops new entries; open positions still exit normally.
          </span>
        </label>

        {err && (
          <div className="text-[11px] text-red-500 space-y-1">
            <div className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {err}</div>
            {blocking.length > 0 && (
              <div className="pl-4">
                Still bound to: {blocking.join(', ')}. Point those at another account first.
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => void save()}
            className="inline-flex items-center gap-1.5 h-8 rounded-md bg-primary px-3 text-xs text-primary-foreground disabled:opacity-50"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {saved && <CheckCircle2 className="h-3.5 w-3.5" />}
            {isNew ? 'Create' : 'Save'}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void remove()}
            className="inline-flex items-center gap-1.5 h-8 rounded-md border px-3 text-xs hover:bg-accent disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" /> {isNew ? 'Cancel' : 'Delete'}
          </button>
          {!isNew && (
            <span className="text-[11px] text-muted-foreground ml-auto">
              {money(account!.startingEquity)} · {account!.mode} · {account!.assetClass}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div className="space-y-1">
    <div className="text-[11px] font-medium">{label}</div>
    {children}
    {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
  </div>
);

export default PaperAccountsSettingsPage;
